// Starting up: which account this browser is signed in to (the account page's refresh cookie and keystore, shared
// because both pages are one origin), whether its vault key is here, and where its projects are kept. Offline, the
// last account's key and cache are used as they are: nothing needs the network to read.
import { forgetLocalPreferences } from '@writing-suite/client/browser/forget.ts';
import { ApiError } from '@writing-suite/client/net/api.ts';
import { indexedDBBackend, Keystore } from '@writing-suite/client/browser/keystore.ts';
import { Session } from '@writing-suite/client/browser/session.ts';
import { IdbBackend, MemoryBackend, type Backend } from './backend.ts';
import { OfflineStore } from './offline.ts';

export interface Account {
  session: Session;
  accountId: string;
  avk: Uint8Array;
  store: OfflineStore;
  /** Started without the relay (offline, or it couldn't be reached). */
  startedOffline: boolean;
  /** This browser's device id: word logs are kept per device ("<day> <device> +|-"). Kept for offline starts. */
  deviceId: string;
}

/** The device id the relay gave this browser, kept for starting offline (else the one kept, else a stand-in). */
async function deviceOf(session: Session, store: OfflineStore): Promise<string> {
  if (session.deviceId) {
    await store.setDevice(session.deviceId).catch(() => {});
    return session.deviceId;
  }
  return (await store.device().catch(() => undefined)) ?? 'web';
}

export type Boot =
  | { kind: 'signedOut'; message?: string }
  | { kind: 'locked'; session: Session; backend: Backend }
  | { kind: 'offlineLocked' }
  | { kind: 'ready'; account: Account };

export const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/** The relay said this browser's session is over (signed out elsewhere, or its token reused). */
export function sessionEnded(e: unknown): boolean {
  // W8 (PROTOCOL.md §9.4, §9.6): a suspended, deleting or deleted account's sessions are over too.
  return e instanceof ApiError && e.status === 401 && ['revoked', 'unauthorised', 'suspended', 'deleting', 'deleted'].includes(e.code);
}

async function dataBackend(keystore: Keystore): Promise<Backend> {
  // "Don't keep the vault key on this computer" keeps the projects in memory too.
  if (await keystore.memoryOnly()) return new MemoryBackend();
  return (await IdbBackend.open()) ?? new MemoryBackend();
}

export async function boot(): Promise<Boot> {
  const keystore = new Keystore(await indexedDBBackend());
  const session = new Session(keystore, location.origin);
  const backend = await dataBackend(keystore);
  const last = await OfflineStore.lastAccount(backend);

  let state: Awaited<ReturnType<Session['resume']>> | 'unreachable';
  if (!isOnline()) state = 'unreachable';
  else {
    try {
      state = await session.resume();
    } catch {
      state = 'unreachable'; // offline, or the relay is down: read from this computer
    }
  }

  if (state === 'unreachable') {
    const avk = last ? await keystore.recall(last) : null;
    if (!last || !avk) return { kind: 'offlineLocked' };
    const store = new OfflineStore(backend, last);
    return { kind: 'ready', account: { session, accountId: last, avk, store, startedOffline: true, deviceId: await deviceOf(session, store) } };
  }
  if (state === 'signedOut') {
    // Signed out (here or from another device): what this computer kept for that account goes too.
    if (last) await new OfflineStore(backend, last).wipe();
    return { kind: 'signedOut' };
  }
  if (last && last !== session.accountId) await new OfflineStore(backend, last).wipe();
  await OfflineStore.setLastAccount(backend, session.accountId);
  if (state === 'locked' || !session.avk) return { kind: 'locked', session, backend };
  const store = new OfflineStore(backend, session.accountId);
  return { kind: 'ready', account: { session, accountId: session.accountId, avk: session.avk, store, startedOffline: false, deviceId: await deviceOf(session, store) } };
}

/** After an unlock in this page. */
export function unlocked(session: Session, backend: Backend): Boot {
  if (!session.avk) return { kind: 'locked', session, backend };
  const store = new OfflineStore(backend, session.accountId);
  void store.setDevice(session.deviceId).catch(() => {});
  return { kind: 'ready', account: { session, accountId: session.accountId, avk: session.avk, store, startedOffline: false, deviceId: session.deviceId || 'web' } };
}

/** This browser was signed out while open: keys and cache go. */
export async function endSession(account: Account): Promise<void> {
  await account.session.forgetLocally().catch(() => {});
  await account.store.wipe().catch(() => {});
  forgetLocalPreferences();
}
